import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import { 
  FaPen, FaPlus, FaFilter, FaDownload, FaSearch, FaTimes, FaEye, 
  FaImage, FaTag, FaPercentage, FaUsers, FaChevronLeft, FaChevronRight,
  FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaArrowLeft, FaArrowRight, FaBars, FaThLarge, FaList, FaSort,
  FaSortUp, FaSortDown, FaEdit, FaTrash, FaCopy, FaBuilding
} from 'react-icons/fa';
import Select from 'react-select';

// Make offer type options smaller
const OFFER_TYPE_OPTIONS = [
  { value: 'spl', label: 'SPL' },
  { value: 'hvr', label: 'HVR' },
  { value: 'nrp', label: 'NRP' },
  { value: 'flat', label: 'FLAT' },
  { value: 'clp', label: 'CLP' },
  { value: 'kap', label: 'KAP' },
  { value: 'sdp', label: 'SDP' }
];

const BrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { user } = useContext(AuthContext);

  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBrands, setTotalBrands] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [nameFilterTimeout, setNameFilterTimeout] = useState(null);
  
  // UI state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [offerTypeFilter, setOfferTypeFilter] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    image: null,
    categories: [],
    supportedOfferTypes: [],
    salespersonBasicIncentive: 0,
    referenceBasicIncentive: 0,
    additionalFields: []
  });

  // Format categories for react-select
  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name
  }));

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Separate useEffect for fetching brands to avoid unnecessary category fetches
  useEffect(() => {
    fetchBrands();
  }, [page, limit, nameFilter]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      Swal.fire('Error', 'Failed to fetch categories', 'error');
    }
  };

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Build query string for pagination and search
      const queryParams = new URLSearchParams({
        page,
        limit
      });
      
      // Only add name filter if it's not empty
      if (nameFilter && nameFilter.trim() !== '') {
        queryParams.set('name', nameFilter.trim());
        console.log('Adding name filter:', nameFilter.trim());
      }
      
      const queryString = queryParams.toString();
      console.log('API query string:', queryString);
      
      const response = await api.get(`/brands/paginated?${queryString}`);
      
      console.log('Paginated brands response:', response.data);
      
      // Handle the response data
      if (response.data && Array.isArray(response.data.brands)) {
        setBrands(response.data.brands);
        setTotalPages(response.data.totalPages || 1);
        setTotalBrands(response.data.totalBrands || 0);
        
        console.log('Set brands count:', response.data.brands.length);
        console.log('Total brands:', response.data.totalBrands);
        console.log('Total pages:', response.data.totalPages);
      } else {
        console.warn('Unexpected response format:', response.data);
        setBrands([]);
        setTotalPages(1);
        setTotalBrands(0);
      }
    } catch (error) {
      console.error('Error fetching brands:', error.response || error);
      setBrands([]);
      setTotalPages(1);
      setTotalBrands(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle name filter change with debouncing
  const handleNameFilterChange = (e) => {
    const value = e.target.value;
    setNameFilter(value);
    
    // Clear any existing timeout
    if (nameFilterTimeout) {
      clearTimeout(nameFilterTimeout);
    }
    
    // Set a new timeout to delay the API call
    const timeoutId = setTimeout(() => {
      setPage(1); // Reset to first page when search changes
      // fetchBrands() will be called via useEffect when nameFilter changes
    }, 300); // 300ms delay
    
    setNameFilterTimeout(timeoutId);
  };

  // Handle pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCategoryChange = (selectedOptions) => {
    setFormData({
      ...formData,
      categories: selectedOptions ? selectedOptions.map(option => option.value) : []
    });
  };

  const handleOfferTypesChange = (selectedOptions) => {
    setFormData({
      ...formData,
      supportedOfferTypes: selectedOptions ? selectedOptions.map(option => option.value) : []
    });
  };

  // Handle additional fields changes
  const handleAddAdditionalField = () => {
    setFormData({
      ...formData,
      additionalFields: [...formData.additionalFields, { fieldName: '', fieldType: 'string', isRequired: false, defaultValue: null }]
    });
  };

  const handleRemoveAdditionalField = (index) => {
    const newFields = formData.additionalFields.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      additionalFields: newFields
    });
  };

  const handleAdditionalFieldChange = (index, field, value) => {
    const newFields = [...formData.additionalFields];
    newFields[index][field] = value;
    setFormData({
      ...formData,
      additionalFields: newFields
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: null,
      categories: [],
      supportedOfferTypes: [],
      salespersonBasicIncentive: 0,
      referenceBasicIncentive: 0,
      additionalFields: []
    });
    setImagePreview(null);
    setCurrentBrand(null);
    setIsEditing(false);
  };

  const handleDeleteBrand = async (brand) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the brand "${brand.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/brands/${brand._id}`);
        Swal.fire('Deleted!', `Brand "${brand.name}" has been deleted.`, 'success');
        fetchBrands();
      } catch (error) {
        console.error('Error deleting brand:', error);
        Swal.fire('Error', 'Failed to delete brand. Please try again.', 'error');
      }
    }
  };

  const openModal = (brand = null) => {
    if (brand) {
      setFormData({
        name: brand.name,
        image: null,
        categories: brand.categories?.map(cat => typeof cat === 'object' ? cat._id : cat) || [],
        supportedOfferTypes: brand.supportedOfferTypes || [],
        salespersonBasicIncentive: brand.salespersonBasicIncentive || 0,
        referenceBasicIncentive: brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive || 0,
        additionalFields: brand.additionalFields || []
      });
      setCurrentBrand(brand);
      setIsEditing(true);
      if (brand._id) {
        setImagePreview(`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`);
      }
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Brand name is required', 'error');
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('salespersonBasicIncentive', formData.salespersonBasicIncentive);
    submitData.append('referenceBasicIncentive', formData.referenceBasicIncentive);
    
    // Handle categories - ensure array is properly sent
    if (formData.categories && formData.categories.length > 0) {
      formData.categories.forEach(categoryId => {
        submitData.append('categories[]', categoryId);
      });
    } else {
      // Send empty array indicator
      submitData.append('categories', JSON.stringify([]));
    }
    
    // Handle supportedOfferTypes - ensure array is properly sent
    if (formData.supportedOfferTypes && formData.supportedOfferTypes.length > 0) {
      formData.supportedOfferTypes.forEach(offerType => {
        submitData.append('supportedOfferTypes[]', offerType);
      });
    } else {
      // Send empty array indicator
      submitData.append('supportedOfferTypes', JSON.stringify([]));
    }
    
    // Handle additionalFields
    if (formData.additionalFields && formData.additionalFields.length > 0) {
      submitData.append('additionalFields', JSON.stringify(formData.additionalFields));
    } else {
      submitData.append('additionalFields', JSON.stringify([]));
    }
    
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    // Debug: Log what we're sending
    console.log('Form data being sent:');
    console.log('- name:', formData.name);
    console.log('- categories:', formData.categories);
    console.log('- supportedOfferTypes:', formData.supportedOfferTypes);
    console.log('- additionalFields:', formData.additionalFields);
    console.log('- image:', formData.image ? 'Present' : 'Not present');

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });

      if (isEditing && currentBrand) {
        await api.put(`/brands/${currentBrand._id}`, submitData);
        Swal.fire('Success', 'Brand updated successfully', 'success');
      } else {
        await api.post('/brands', submitData);
        Swal.fire('Success', 'Brand created successfully', 'success');
      }

      closeModal();
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save brand';
      Swal.fire('Error', errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setNameFilter('');
    setPage(1);
  };

  // Export brands function
  const exportBrands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const response = await api.get('/brands/export', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `brands_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire('Success', 'Brands exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting brands:', error);
      Swal.fire('Error', 'Failed to export brands', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 shadow-lg border-b border-purple-200">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                  <FaBuilding className="text-white text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Brand Management</h1>
                  <p className="text-blue-100 mt-1 text-sm">Manage your brand portfolio with style</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Stats */}
              <div className="hidden md:flex items-center space-x-6 mr-6">
                <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1">
                  <div className="text-lg font-bold text-white">{totalBrands}</div>
                  <div className="text-xs text-blue-100">Total Brands</div>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1">
                  <div className="text-lg font-bold text-white">{categories.length}</div>
                  <div className="text-xs text-blue-100">Categories</div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <button
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                onClick={exportBrands}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <FaSpinner className="animate-spin mr-2" size={14} />
                ) : (
                  <FaDownload className="mr-2" size={14} />
                )}
                Export
              </button>
              
              <button
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-medium rounded-lg hover:from-amber-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                onClick={() => openModal()}
              >
                <FaPlus className="mr-2" size={14} />
                New Brand
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Enhanced Filter Section */}
        <div className="bg-gradient-to-r from-white to-blue-50 rounded-2xl shadow-lg border border-blue-100 mb-8">
          <div className="p-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <FaFilter className="mr-2" size={16} />
                  Filters
                  {showFilters && <FaTimes className="ml-2" size={14} />}
                </button>
                
                {/* View Mode Toggle */}
                <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-3 font-medium transition-all duration-200 ${
                      viewMode === 'grid'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaThLarge size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-3 font-medium transition-all duration-200 ${
                      viewMode === 'list'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaList size={16} />
                  </button>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search brands..."
                    value={nameFilter}
                    onChange={handleNameFilterChange}
                    className="w-full pl-12 pr-12 py-3 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm text-sm placeholder-blue-300"
                  />
                  {nameFilter && (
                    <button
                      onClick={() => setNameFilter('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-blue-600 bg-blue-50 rounded-full p-1 transition-colors"
                    >
                      <FaTimes size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Expanded Filters */}
            {showFilters && (
              <div className="border-t border-gradient-to-r from-blue-200 to-purple-200 pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                    <label className="block text-sm font-semibold text-blue-700 mb-3">Category</label>
                    <Select
                      value={categoryFilter ? { value: categoryFilter, label: categories.find(c => c._id === categoryFilter)?.name || '' } : null}
                      onChange={(option) => setCategoryFilter(option?.value || '')}
                      options={[
                        { value: '', label: 'All Categories' },
                        ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                      ]}
                      isClearable
                      placeholder="Select category..."
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.75rem',
                          border: '2px solid #DBEAFE',
                          '&:hover': { borderColor: '#3B82F6' }
                        })
                      }}
                    />
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                    <label className="block text-sm font-semibold text-green-700 mb-3">Offer Type</label>
                    <Select
                      value={offerTypeFilter ? { value: offerTypeFilter, label: OFFER_TYPE_OPTIONS.find(o => o.value === offerTypeFilter)?.label || '' } : null}
                      onChange={(option) => setOfferTypeFilter(option?.value || '')}
                      options={[
                        { value: '', label: 'All Offer Types' },
                        ...OFFER_TYPE_OPTIONS
                      ]}
                      isClearable
                      placeholder="Select offer type..."
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.75rem',
                          border: '2px solid #D1FAE5',
                          '&:hover': { borderColor: '#10B981' }
                        })
                      }}
                    />
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
                    <label className="block text-sm font-semibold text-purple-700 mb-3">Sort By</label>
                    <div className="flex space-x-3">
                      <Select
                        value={{ value: sortBy, label: sortBy.charAt(0).toUpperCase() + sortBy.slice(1) }}
                        onChange={(option) => setSortBy(option.value)}
                        options={[
                          { value: 'name', label: 'Name' },
                          { value: 'createdAt', label: 'Created Date' }
                        ]}
                        className="flex-1 text-sm"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderRadius: '0.75rem',
                            border: '2px solid #E9D5FF',
                            '&:hover': { borderColor: '#8B5CF6' }
                          })
                        }}
                      />
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all duration-200 shadow-md"
                      >
                        {sortOrder === 'asc' ? <FaSortUp size={16} /> : <FaSortDown size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Clear Filters */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setNameFilter('');
                      setCategoryFilter('');
                      setOfferTypeFilter('');
                      setSortBy('name');
                      setSortOrder('asc');
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-red-400 to-pink-500 text-white font-medium rounded-xl hover:from-red-500 hover:to-pink-600 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
            <div className="relative">
              <input
                type="text"
                value={nameFilter}
                onChange={handleNameFilterChange}
                className="w-full p-2 pl-10 border rounded"
                placeholder="Search brands by name..."
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-4">Loading...</div>}

      {!loading && brands.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No brands found. Create a new brand to get started.</p>
        </div>
      )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="flex items-center space-x-3">
              <FaSpinner className="animate-spin text-blue-600" size={24} />
              <span className="text-gray-600">Loading brands...</span>
            </div>
          </div>
        )}

        {/* Brands Display */}
        {!loading && brands.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {brands.map((brand) => (
                  <div key={brand._id} className="group bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-2xl hover:border-purple-300 transition-all duration-300 overflow-hidden transform hover:-translate-y-2">
                    {/* Brand Image */}
                    <div className="h-56 bg-gradient-to-br from-blue-100 via-purple-50 to-pink-100 flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 group-hover:from-blue-500/20 group-hover:to-purple-500/20 transition-all duration-300"></div>
                      <img 
                        src={`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`} 
                        alt={brand.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="text-center" style={{display: 'none'}}>
                        <FaImage className="mx-auto text-gray-400 mb-2" size={40} />
                        <span className="text-gray-500 text-sm">No Image</span>
                      </div>
                      
                      {/* Brand ID Badge */}
                      <div className="absolute top-3 right-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg">
                        {brand.id || brand._id.toString().substring(0, 8)}
                      </div>
                    </div>
                    
                    {/* Brand Info */}
                    <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-gray-900 truncate group-hover:text-purple-700 transition-colors">{brand.name}</h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openModal(brand)}
                            className="p-3 text-blue-600 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                            title="Edit Brand"
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteBrand(brand)}
                            className="p-3 text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                            title="Delete Brand"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Categories */}
                      {brand.categories && brand.categories.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {brand.categories.slice(0, 3).map((category, index) => {
                              const categoryId = category._id || category;
                              const categoryData = categories.find(c => c._id === categoryId);
                              return categoryData ? (
                                <span 
                                  key={index} 
                                  className="inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md hover:shadow-lg transition-shadow"
                                >
                                  <FaTag className="mr-1" size={12} />
                                  {categoryData.name}
                                </span>
                              ) : null;
                            })}
                            {brand.categories.length > 3 && (
                              <span className="inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-md">
                                +{brand.categories.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Offer Types */}
                      {brand.supportedOfferTypes && brand.supportedOfferTypes.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {brand.supportedOfferTypes.slice(0, 3).map((offerType, index) => (
                              <span 
                                key={index} 
                                className="inline-flex items-center px-3 py-2 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md hover:from-green-600 hover:to-emerald-600 transition-all"
                              >
                                {offerType.toUpperCase()}
                              </span>
                            ))}
                            {brand.supportedOfferTypes.length > 3 && (
                              <span className="inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-md">
                                +{brand.supportedOfferTypes.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Incentives */}
                      {(brand.salespersonBasicIncentive > 0 || brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                        <div className="border-t-2 border-gradient-to-r from-blue-200 to-purple-200 pt-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 mt-2">
                          <div className="flex items-center justify-between text-sm">
                            {brand.salespersonBasicIncentive > 0 && (
                              <div className="flex items-center space-x-2 bg-blue-100 px-3 py-2 rounded-full">
                                <FaUsers className="text-blue-700" size={14} />
                                <span className="text-blue-700 font-medium">Sales:</span>
                                <span className="font-bold text-blue-800 bg-blue-200 px-2 py-1 rounded-full">{brand.salespersonBasicIncentive}%</span>
                              </div>
                            )}
                            {(brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                              <div className="flex items-center space-x-2 bg-purple-100 px-3 py-2 rounded-full">
                                <FaPercentage className="text-purple-700" size={14} />
                                <span className="text-purple-700 font-medium">Reference:</span>
                                <span className="font-bold text-purple-800 bg-purple-200 px-2 py-1 rounded-full">
                                  {brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive}%
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {brands.map((brand) => (
                    <div key={brand._id} className="p-8 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 group">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6">
                          {/* Brand Image */}
                          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center overflow-hidden shadow-md group-hover:shadow-lg transition-shadow">
                            <img 
                              src={`${process.env.REACT_APP_API_BASE_URL}/brands/image/${brand._id}?t=${Date.now()}`} 
                              alt={brand.name}
                              className="w-full h-full object-cover rounded-2xl group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div style={{display: 'none'}}>
                              <FaImage className="text-gray-400" size={28} />
                            </div>
                          </div>
                          
                          {/* Brand Info */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-4">
                              <h3 className="text-2xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors">{brand.name}</h3>
                              <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-full text-sm font-bold shadow-md">
                                {brand.id || brand._id.toString().substring(0, 8)}
                              </span>
                            </div>
                            
                            <div className="mt-1 flex items-center space-x-4">
                              {/* Categories */}
                              {brand.categories && brand.categories.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <FaTag className="text-gray-400" size={12} />
                                  <span className="text-sm text-gray-600">
                                    {brand.categories.slice(0, 2).map(cat => {
                                      const categoryId = cat._id || cat;
                                      const categoryData = categories.find(c => c._id === categoryId);
                                      return categoryData ? categoryData.name : null;
                                    }).filter(Boolean).join(', ')}
                                    {brand.categories.length > 2 && ` +${brand.categories.length - 2} more`}
                                  </span>
                                </div>
                              )}
                              
                              {/* Offer Types */}
                              {brand.supportedOfferTypes && brand.supportedOfferTypes.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm text-gray-600">
                                    {brand.supportedOfferTypes.slice(0, 2).map(type => type.toUpperCase()).join(', ')}
                                    {brand.supportedOfferTypes.length > 2 && ` +${brand.supportedOfferTypes.length - 2} more`}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Incentives */}
                            {(brand.salespersonBasicIncentive > 0 || brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                              <div className="mt-2 flex items-center space-x-4">
                                {brand.salespersonBasicIncentive > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <FaUsers className="text-blue-600" size={12} />
                                    <span className="text-sm text-gray-600">Sales: {brand.salespersonBasicIncentive}%</span>
                                  </div>
                                )}
                                {(brand.referenceBasicIncentive > 0 || brand.architectInteriorDesignerBasicIncentive > 0 || brand.architectInteriorBasicIncentive > 0) && (
                                  <div className="flex items-center space-x-1">
                                    <FaPercentage className="text-purple-600" size={12} />
                                    <span className="text-sm text-gray-600">
                                      Reference: {brand.referenceBasicIncentive || brand.architectInteriorDesignerBasicIncentive || brand.architectInteriorBasicIncentive}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex space-x-3">
                          <button
                            onClick={() => openModal(brand)}
                            className="p-3 text-blue-600 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                            title="Edit Brand"
                          >
                            <FaEdit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteBrand(brand)}
                            className="p-3 text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                            title="Delete Brand"
                          >
                            <FaTrash size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && brands.length === 0 && (
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-200">
            <div className="p-16 text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No brands found</h3>
              <p className="text-gray-600 mb-8 text-lg">
                {nameFilter ? 'Try adjusting your search criteria' : 'Get started by creating your first brand'}
              </p>
              <button
                onClick={() => openModal()}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <FaPlus className="mr-3" size={18} />
                Create Brand
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-r from-white to-blue-50 rounded-2xl shadow-lg border border-blue-200 p-6 mt-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="text-lg text-gray-700 mb-4 sm:mb-0 bg-blue-50 px-4 py-2 rounded-xl">
                Showing <span className="font-bold text-blue-700">{Math.min((page - 1) * limit + 1, totalBrands)}</span> to{' '}
                <span className="font-bold text-blue-700">{Math.min(page * limit, totalBrands)}</span> of{' '}
                <span className="font-bold text-purple-700">{totalBrands}</span> results
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <FaChevronLeft className="mr-2" size={14} />
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                    const showPageNum = 
                      pageNum === 1 || 
                      pageNum === totalPages || 
                      (pageNum >= page - 1 && pageNum <= page + 1);
                    
                    if (!showPageNum) {
                      if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <span key={`ellipsis-${pageNum}`} className="px-4 py-3 text-blue-400 font-bold text-lg">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-4 py-3 rounded-xl font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${
                          pageNum === page
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white scale-110'
                            : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 border-2 border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  Next
                  <FaChevronRight className="ml-2" size={14} />
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Enhanced Brand Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900/60 to-blue-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-3xl">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FaBuilding className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold">
                  {isEditing ? 'Edit Brand' : 'Create New Brand'}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200"
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Brand Name */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border border-blue-200">
                <label className="block text-sm font-semibold text-blue-700 mb-3">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                  placeholder="Enter brand name"
                  required
                />
              </div>

              {/* Brand Image */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
                <label className="block text-sm font-semibold text-green-700 mb-3">
                  Brand Image {!isEditing && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center space-x-6">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                      required={!isEditing}
                    />
                  </div>
                  {imagePreview && (
                    <div className="w-24 h-24 border-2 border-green-300 rounded-2xl overflow-hidden shadow-lg">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200">
                <label className="block text-sm font-semibold text-purple-700 mb-3">
                  Categories
                </label>
                <Select
                  isMulti
                  name="categories"
                  options={categoryOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={categoryOptions.filter(option => 
                    formData.categories.includes(option.value)
                  )}
                  onChange={handleCategoryChange}
                  placeholder="Select categories..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '0.75rem',
                      border: '2px solid #E9D5FF',
                      padding: '0.25rem',
                      '&:hover': { borderColor: '#8B5CF6' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#8B5CF6',
                      borderRadius: '1rem',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 'bold'
                    })
                  }}
                />
              </div>

              {/* Supported Offer Types */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-200">
                <label className="block text-sm font-semibold text-amber-700 mb-3">
                  Supported Offer Types
                </label>
                <Select
                  isMulti
                  name="supportedOfferTypes"
                  options={OFFER_TYPE_OPTIONS}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={OFFER_TYPE_OPTIONS.filter(option => 
                    formData.supportedOfferTypes.includes(option.value)
                  )}
                  onChange={handleOfferTypesChange}
                  placeholder="Select supported offer types..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '0.75rem',
                      border: '2px solid #FDE68A',
                      padding: '0.25rem',
                      '&:hover': { borderColor: '#F59E0B' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#F59E0B',
                      borderRadius: '1rem',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 'bold'
                    })
                  }}
                />
              </div>

              {/* Incentive Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-2xl border border-cyan-200">
                  <label className="block text-lg font-semibold text-cyan-700 mb-3">
                    Salesperson Basic Incentive (%)
                  </label>
                  <input
                    type="number"
                    name="salespersonBasicIncentive"
                    value={formData.salespersonBasicIncentive}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-4 py-3 border-2 border-cyan-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 text-lg"
                    placeholder="Enter percentage (0-100)"
                  />
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl border border-indigo-200">
                  <label className="block text-lg font-semibold text-indigo-700 mb-3">
                    Reference Basic Incentive (%)
                  </label>
                  <input
                    type="number"
                    name="referenceBasicIncentive"
                    value={formData.referenceBasicIncentive}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-4 py-2 border-2 border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm"
                    placeholder="Enter percentage (0-100)"
                  />
                </div>
              </div>

              {/* Additional Fields Section */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-200">
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-lg font-semibold text-emerald-700">
                    Additional Fields
                  </label>
                  <button
                    type="button"
                    onClick={handleAddAdditionalField}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200 text-sm font-medium"
                  >
                    + Add Field
                  </button>
                </div>

                {formData.additionalFields.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No additional fields added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {formData.additionalFields.map((field, index) => (
                      <div key={index} className="bg-white p-4 rounded-xl border border-emerald-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Field Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={field.fieldName}
                              onChange={(e) => handleAdditionalFieldChange(index, 'fieldName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Enter field name"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Field Type
                            </label>
                            <select
                              value={field.fieldType}
                              onChange={(e) => handleAdditionalFieldChange(index, 'fieldType', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="string">Text</option>
                              <option value="number">Number</option>
                            </select>
                          </div>

                          <div className="flex items-end">
                            <div className="flex items-center space-x-2 mb-2">
                              <input
                                type="checkbox"
                                checked={field.isRequired || false}
                                onChange={(e) => handleAdditionalFieldChange(index, 'isRequired', e.target.checked)}
                                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                              />
                              <label className="text-sm text-gray-700">Required</label>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAdditionalField(index)}
                              className="ml-auto px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t-2 border-gradient-to-r from-blue-200 to-purple-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 font-medium text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 border-2 border-gray-300 rounded-xl hover:from-gray-200 hover:to-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-6 py-2 font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      {isEditing ? (
                        <>
                          <FaEdit className="mr-2" size={16} />
                          Update Brand
                        </>
                      ) : (
                        <>
                          <FaPlus className="mr-2" size={16} />
                          Create Brand
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandManagement;